import React, { useEffect, useState } from 'react';
import { Lock, ShieldCheck, Fingerprint } from 'lucide-react';

/**
 * Güvenlik doğrulama animasyonu bileşeni
 * Kullanıcının güvenli alanlara erişimini göstermek için görsel geri bildirim sağlar
 * 
 * @param {Object} props - Bileşen özellikleri
 * @param {boolean} props.show - Bileşenin gösterilip gösterilmeyeceği
 * @param {Function} props.onComplete - Doğrulama tamamlandığında çağrılacak fonksiyon
 * @param {number} props.duration - Animasyon süresi (milisaniye cinsinden)
 * @param {string} props.message - Gösterilecek mesaj
 */
const SecurityVerification = ({ 
  show, 
  onComplete, 
  duration = 1500,
  message = 'Dijital Kasa\'nıza erişmek için kimliğiniz doğrulanıyor.'
}) => {
  const [step, setStep] = useState(0);
  const [isVerified, setIsVerified] = useState(false);

  // Animasyon zamanlaması
  useEffect(() => {
    if (!show) {
      setStep(0);
      setIsVerified(false);
      return;
    }

    // Adımların zamanlamasını ayarla
    const step1Timer = setTimeout(() => setStep(1), 300);
    const step2Timer = setTimeout(() => setStep(2), 600);
    const verifiedTimer = setTimeout(() => setIsVerified(true), duration - 500);
    
    // Tamamlama işlevini çağır
    const completeTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, duration);

    return () => {
      clearTimeout(step1Timer);
      clearTimeout(step2Timer);
      clearTimeout(verifiedTimer);
      clearTimeout(completeTimer);
    };
  }, [show, duration, onComplete]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-blue-900/95 z-50 flex flex-col items-center justify-center text-white">
      <div className="relative">
        {/* Ana simge */}
        <div className="relative mb-6">
          {step < 2 ? (
            <Lock size={64} className={`transition-opacity duration-300 ${step >= 0 ? 'opacity-100' : 'opacity-0'}`} />
          ) : (
            <ShieldCheck size={64} className="text-green-400" />
          )}
          
          {/* Parmak izi tarama animasyonu */}
          {step >= 1 && step < 2 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Fingerprint size={32} className="text-blue-400 animate-pulse" />
            </div>
          )}
        </div>

        {/* Başlık */}
        <h2 className="text-2xl font-bold mb-2">
          {isVerified ? 'Kimliğiniz Doğrulandı' : 'Güvenlik Doğrulaması'}
        </h2>
        
        {/* Mesaj */}
        <p className="text-center max-w-md mb-6">
          {isVerified ? 'Güvenli bağlantı kuruldu. Dijital Kasa\'nıza yönlendiriliyorsunuz.' : message}
        </p>
        
        {/* Yükleme animasyonu */}
        {!isVerified ? (
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mb-4"></div>
        ) : (
          <div className="w-16 h-16 flex items-center justify-center mb-4">
            <div className="w-full h-full relative">
              <div className="absolute inset-0 border-4 border-green-400 rounded-full"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <ShieldCheck size={32} className="text-green-400" />
              </div>
            </div>
          </div>
        )}
        
        {/* Durum metni */}
        <p className="text-sm opacity-70">
          {isVerified ? 'Yönlendiriliyor...' : 'Güvenli bağlantı kuruluyor...'}
        </p>
      </div>
    </div>
  );
};

export default SecurityVerification;
